import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import ProductCard from '@/components/ProductCard';
import type { Product } from '@/types/product';

export default function TraditionalHome() {
  const [bestsellers, setBestsellers] = useState<Product[]>([]);
  const [newArrivals, setNewArrivals] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      // Load bestsellers
      const bestQuery = query(
        collection(db, 'products'),
        where('isBestseller', '==', true),
        limit(6)
      );
      const bestSnap = await getDocs(bestQuery);
      const bestProducts = bestSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
      setBestsellers(bestProducts);

      // Load new arrivals
      const newQuery = query(
        collection(db, 'products'),
        where('isNew', '==', true),
        limit(6)
      );
      const newSnap = await getDocs(newQuery);
      const newProducts = newSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product));
      setNewArrivals(newProducts);

      setLoading(false);
    } catch (error) {
      console.error('Error loading products:', error);
      setLoading(false);
    }
  };

  return (
    <div className="space-y-16 pb-16">
      {/* Hero */}
      <section className="relative h-[600px] bg-gradient-to-br from-forest-dark to-forest overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://picsum.photos/seed/hero/1920/1080"
            alt="Mountain landscape"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="max-w-2xl">
            <h1 className="text-6xl font-serif font-bold text-warm mb-6 leading-tight">
              Gear for the journey ahead
            </h1>
            <p className="text-xl text-warm-dark mb-8">
              Premium equipment for those who seek the path less travelled. From trail to summit, we've got you covered.
            </p>
            <div className="flex gap-4">
              <Link
                to="/category/new"
                className="px-8 py-4 bg-amber text-white font-semibold rounded-lg hover:bg-amber-dark transition-colors"
              >
                Shop New Arrivals
              </Link>
              <Link
                to="/category/sale"
                className="px-8 py-4 bg-white text-forest font-semibold rounded-lg hover:bg-warm transition-colors"
              >
                View Sale
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { name: 'Footwear', image: 'footwear', path: '/category/footwear' },
            { name: 'Outerwear', image: 'outerwear', path: '/category/outerwear' },
            { name: 'Backpacks', image: 'backpacks', path: '/category/backpacks' },
          ].map(category => (
            <Link
              key={category.name}
              to={category.path}
              className="group relative h-80 rounded-lg overflow-hidden shadow-card hover:shadow-card-hover transition-all"
            >
              <img
                src={`https://picsum.photos/seed/${category.image}/600/800`}
                alt={category.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end">
                <h3 className="text-3xl font-serif font-bold text-white p-6">{category.name}</h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Bestsellers */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-4xl font-serif font-bold text-slate mb-2">Bestsellers</h2>
            <p className="text-gray-600">Our most popular gear, trusted by adventurers worldwide</p>
          </div>
          <Link to="/category/bestsellers" className="text-amber hover:text-amber-dark font-medium">
            View all →
          </Link>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-12 h-12 border-4 border-forest border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {bestsellers.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>

      {/* New Arrivals */}
      <section className="bg-warm-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-4xl font-serif font-bold text-slate mb-2">New Arrivals</h2>
              <p className="text-gray-600">The latest gear to enhance your outdoor adventures</p>
            </div>
            <Link to="/category/new" className="text-amber hover:text-amber-dark font-medium">
              View all →
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {newArrivals.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Brand Strip */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h3 className="text-center text-sm uppercase tracking-wider text-gray-500 mb-8">
          Featuring Premium Brands
        </h3>
        <div className="flex flex-wrap justify-center items-center gap-12 opacity-60">
          {['Osprey', 'Salomon', 'Arc\'teryx', 'Black Diamond', 'Mammut', 'Petzl'].map(brand => (
            <span key={brand} className="text-2xl font-bold text-slate">{brand}</span>
          ))}
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-forest text-warm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h2 className="text-3xl font-serif font-bold mb-4">Join the Trail Community</h2>
          <p className="text-warm-dark mb-8">
            Get exclusive access to new gear, trail stories, and special offers
          </p>
          <form className="flex gap-4 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg bg-forest-light text-warm placeholder-warm-dark focus:outline-none focus:ring-2 focus:ring-amber"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-amber text-white font-semibold rounded-lg hover:bg-amber-dark transition-colors"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
